import { ErrorHandler, Injector, Injectable } from '@angular/core';
import { ApplicationInsightsService } from './applicationinsights.service';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { LocalizationParam } from '@abp/ng.core';
import { HttpErrorResponse } from '@angular/common/http';

export const DEFAULT_ERROR_MESSAGES = {
  defaultError: {
    title: 'An error has occurred!',
    details: 'Error detail not sent by server.',
  },
  defaultError401: {
    title: 'You are not authenticated!',
    details: 'You should be authenticated (sign in) in order to perform this operation.',
  },
  defaultError403: {
    title: 'You are not authorized!',
    details: 'You are not allowed to perform this operation.',
  },
  defaultError404: {
    title: 'Resource not found!',
    details: 'The resource requested could not found on the server.',
  },
  defaultError500: {
    title: 'Internal server error',
    details: 'Error detail not sent by server.',
  },
  defaultErrorConnection: {
    title: 'Unable to reach the server',
    details: 'Please check your internet connection and try again.',
  },
};

export const DEFAULT_ERROR_LOCALIZATIONS = {
  defaultError: {
    title: 'AbpUi::DefaultErrorMessage',
    details: 'AbpUi::DefaultErrorMessageDetail',
  },
  defaultError401: {
    title: 'AbpUi::DefaultErrorMessage401',
    details: 'AbpUi::DefaultErrorMessage401Detail',
  },
  defaultError403: {
    title: 'AbpUi::DefaultErrorMessage403',
    details: 'AbpUi::DefaultErrorMessage403Detail',
  },
  defaultError404: {
    title: 'AbpUi::DefaultErrorMessage404',
    details: 'AbpUi::DefaultErrorMessage404Detail',
  },
  defaultError500: {
    title: 'AbpUi::500Message',
    details: 'AbpUi::DefaultErrorMessage',
  },
  defaultErrorConnection: {
    title: 'AbpUi::DefaultErrorMessageConnectionTitle',
    details: 'AbpUi::DefaultErrorMessageConnectionDetail',
  },
};

interface LocalizationParameter {
  key: string;
  defaultValue: string;
}

@Injectable()
export class AppErrorHandler implements ErrorHandler {
  constructor(
    private injector: Injector,
    private notification: NzNotificationService
  ) {}

  navigateToLogin() {}

  handleError(err: HttpErrorResponse): void {
    this.injector.get<ApplicationInsightsService>(ApplicationInsightsService).logException(err);

    if (err.status == null) return;

    if (err?.error?.error?.message) {
      this.notification.error(err.error.error.message, err.error.error.details, {
        nzDuration: 10000,
      });
    } else {
      switch (err.status) {
        case 401:
          this.showError(
            {
              key: DEFAULT_ERROR_LOCALIZATIONS.defaultError401.title,
              defaultValue: DEFAULT_ERROR_MESSAGES.defaultError401.title,
            },
            {
              key: DEFAULT_ERROR_LOCALIZATIONS.defaultError401.details,
              defaultValue: DEFAULT_ERROR_MESSAGES.defaultError401.details,
            }
          ).onClose.subscribe(() => this.navigateToLogin());
          break;
        case 403:
          this.showError(
            {
              key: DEFAULT_ERROR_LOCALIZATIONS.defaultError403.details,
              defaultValue: DEFAULT_ERROR_MESSAGES.defaultError403.details,
            },
            {
              key: DEFAULT_ERROR_LOCALIZATIONS.defaultError403.title,
              defaultValue: DEFAULT_ERROR_MESSAGES.defaultError403.title,
            }
          );
          break;
        case 404:
          this.showError(
            {
              key: DEFAULT_ERROR_LOCALIZATIONS.defaultError404.details,
              defaultValue: DEFAULT_ERROR_MESSAGES.defaultError404.details,
            },
            {
              key: DEFAULT_ERROR_LOCALIZATIONS.defaultError404.title,
              defaultValue: DEFAULT_ERROR_MESSAGES.defaultError404.title,
            }
          );
          break;
        case 500:
          this.showError(
            {
              key: DEFAULT_ERROR_LOCALIZATIONS.defaultError500.details,
              defaultValue: DEFAULT_ERROR_MESSAGES.defaultError500.details,
            },
            {
              key: DEFAULT_ERROR_LOCALIZATIONS.defaultError500.title,
              defaultValue: DEFAULT_ERROR_MESSAGES.defaultError500.title,
            }
          );
          break;
        case 0:
          if (err.statusText === 'Unknown Error') {
            this.showError(
              {
                key: DEFAULT_ERROR_LOCALIZATIONS.defaultErrorConnection.details,
                defaultValue: DEFAULT_ERROR_MESSAGES.defaultErrorConnection.details,
              },
              {
                key: DEFAULT_ERROR_LOCALIZATIONS.defaultErrorConnection.title,
                defaultValue: DEFAULT_ERROR_MESSAGES.defaultErrorConnection.title,
              }
            );
          }
          break;
        default:
          this.showError(
            {
              key: DEFAULT_ERROR_LOCALIZATIONS.defaultError.details,
              defaultValue: DEFAULT_ERROR_MESSAGES.defaultError.details,
            },
            {
              key: DEFAULT_ERROR_LOCALIZATIONS.defaultError.title,
              defaultValue: DEFAULT_ERROR_MESSAGES.defaultError.title,
            }
          );
          break;
      }
    }
  }

  private showError(
    message?: LocalizationParam | LocalizationParameter | string,
    title?: LocalizationParam | LocalizationParameter | string,
    body?: any
  ) {
    if (body) {
      if (body.details) {
        message = body.details;
        title = body.message;
      } else if (body.message) {
        title = {
          key: DEFAULT_ERROR_LOCALIZATIONS.defaultError.title,
          defaultValue: DEFAULT_ERROR_MESSAGES.defaultError.title,
        };
        message = body.message;
      } else {
        message = body.message || {
          key: DEFAULT_ERROR_LOCALIZATIONS.defaultError.title,
          defaultValue: DEFAULT_ERROR_MESSAGES.defaultError.title,
        };
      }
    }

    const titleDefaultValue = (title as any)?.defaultValue || title;
    const messageDefaultValue = (message as any)?.defaultValue || message;
    return this.notification.error(titleDefaultValue, messageDefaultValue, {
      nzDuration: 5000,
    });
  }
}
